// Default to the public API host so even same-origin loads hit the backend, not the frontend.
const SAME_ORIGIN_BASE = "https://api.lexicompanion.com/lexi";
const API_OVERRIDE_PARAM = "apiBase";
const SESSION_STORAGE_KEY = "LEXI_ALPHA_SESSION";
const GOOGLE_CLIENT_ID_RUNTIME_KEY = "__LEX_GOOGLE_CLIENT_ID";

function resolveApiBase(): string {
  if (typeof window === "undefined") {
    return SAME_ORIGIN_BASE;
  }
  try {
    const runtime =
      (window as any).__LEX_API_BASE ||
      (window as any).__LEX_API_BASE_PUBLIC ||
      (window as any).__LEX_API_BASE_URL;
    if (runtime && typeof runtime === "string") {
      return runtime.replace(/\/+$/, "");
    }
  } catch {
    // ignore runtime globals
  }
  try {
    const buildTime =
      process.env.REACT_APP_API_URL ||
      process.env.REACT_APP_BACKEND_URL ||
      process.env.REACT_APP_BASE_URL;
    if (buildTime && buildTime.trim()) {
      return buildTime.replace(/\/+$/, "");
    }
  } catch {
    // ignore build-time env access
  }
  try {
    const current = new URL(window.location.href);
    const override = current.searchParams.get(API_OVERRIDE_PARAM);
    if (override) {
      return override.replace(/\/+$/, "");
    }
  } catch {
    // ignore URL parsing issues, fall through to default
  }
  return SAME_ORIGIN_BASE;
}

function resolveGoogleClientId(): string {
  if (typeof window === "undefined") {
    return "";
  }
  try {
    const runtime = (window as any)[GOOGLE_CLIENT_ID_RUNTIME_KEY];
    if (typeof runtime === "string" && runtime.trim()) {
      return runtime.trim();
    }
  } catch {
    // ignore runtime globals
  }
  try {
    const config = (window as any).RUNTIME_CONFIG;
    const fallback = config?.GOOGLE_CLIENT_ID;
    if (typeof fallback === "string" && fallback.trim()) {
      return fallback.trim();
    }
  } catch {
    // ignore runtime globals
  }
  return "";
}

let API_BASE = resolveApiBase();
let configLoaded = false;
let cachedSessionId: string | null = null;
let cachedAuthUserId: string | null = null;

export function setSessionId(id: string | null) {
  cachedSessionId = id;
  if (typeof window !== "undefined") {
    try {
      if (id) {
        window.sessionStorage?.setItem(SESSION_STORAGE_KEY, id);
      } else {
        window.sessionStorage?.removeItem(SESSION_STORAGE_KEY);
      }
    } catch {
      // sessionStorage might be blocked; ignore
    }
  }
}

export function getSessionId(): string | null {
  if (cachedSessionId) {
    return cachedSessionId;
  }
  if (typeof window !== "undefined") {
    try {
      const stored = window.sessionStorage?.getItem(SESSION_STORAGE_KEY);
      if (stored) {
        cachedSessionId = stored;
        return stored;
      }
    } catch {
      // ignore
    }
  }
  return cachedSessionId;
}

/**
 * Compatibility shim for legacy tour storage helpers.
 * This is in-memory only and is never used as an auth source.
 */
export function getUserId(): string | null {
  return cachedAuthUserId;
}

function rememberAuthUserId(user: { id?: string } | null | undefined) {
  cachedAuthUserId = user?.id || null;
}

export async function loadApiConfig() {
  if (configLoaded) return API_BASE;
  API_BASE = resolveApiBase();
  configLoaded = true;
  console.info("[API] Using API_BASE:", API_BASE, {
    buildMode: process.env.NODE_ENV || "unknown",
    credentials: "include",
    chatEndpoint: "/process",
  });
  return API_BASE;
}

export function getGoogleClientId(): string {
  return resolveGoogleClientId();
}

export async function apiFetch(path: string, init: RequestInit = {}) {
  await loadApiConfig();

  const headers = new Headers(init.headers || undefined);
  if (!headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const sessionHeader = getSessionId();
  if (sessionHeader && !headers.has("X-Lexi-Session")) {
    headers.set("X-Lexi-Session", sessionHeader);
  }

  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  return fetch(url, { ...init, headers, credentials: "include" });
}

export function apiUrl(path: string): string {
  if (path.startsWith("http")) return path;
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${API_BASE}${normalized}`;
}

export const PERSONA_PREFIX = "/persona";

export type Traits = Record<string, string>;

export interface PersonaState {
  traits: Traits;
  certainty: number;
  image_path: string;
}

export interface IntentResult {
  intent: "avatar_flow" | "avatar_edit" | "new_look" | "describe_avatar" | "chat" | string;
}

export interface AvatarReviewCandidate {
  index?: number;
  global_index: number;
  round?: number;
  seed?: number;
  judge_status?: string;
  hard_fail_reasons?: string[];
  soft_fail_reasons?: string[];
  image_url: string;
}

export interface AvatarReviewPack {
  id: string;
  counts?: Record<string, number>;
  candidates?: AvatarReviewCandidate[];
  selected_output?: string | null;
  operator_state?: Record<string, any>;
}

export interface TraitResponse {
  ready: boolean;
  narration?: string;
  prompt: string;
  negative?: string;
  persona: {
    traits: Record<string, string>;
    certainty: number;
    image_path: string;
  };
  added?: boolean;
}

export interface AvatarStepResponse {
  ready: boolean;
  persona: PersonaState;
  prompt: string;
  negative: string;
  narration: string;
}

export interface DebugTraitsResponse {
  file_traits: Traits;
  persona_traits: Traits;
}

export interface AnalyticsSummary {
  unique_all_time: number;
  unique_today: number;
  concurrent_now: number;
  peak_concurrent_today: number;
  day: string;
}

export type AvatarGenMode = "txt2img" | "img2img";

export type LexiverseStyle = "promo";

export type Hair = "brunette" | "blonde" | "redhead" | "black";
export type HairStyle = "straight" | "wavy" | "curly" | "updo";
export type SkinTone = "fair" | "light_medium" | "olive" | "tan" | "deep";
export type EyeColor = "brown" | "hazel" | "green" | "blue";
export type Outfit = "lbd" | "lounge" | "casual" | "business" | "sporty";
export type Vibe = "soft" | "playful" | "elegant" | "confident" | "sultry";

export type AvatarTraits = {
  hair?: Hair;
  hair_style?: HairStyle;
  skin_tone?: SkinTone;
  eyes?: EyeColor;
  outfit?: Outfit;
  vibe?: Vibe;
};

export interface AvatarGenRequestPayload {
  description?: string;
  request_id?: string;
  prompt?: string;
  negative_prompt?: string;
  sd_mode?: AvatarGenMode;
  lexiverse_style?: LexiverseStyle;
  seed?: number | null;
  strength?: number;
  flux_pipeline?: string;
  traits?: AvatarTraits;
  extra_details?: string;
}

export interface AvatarGenResponse {
  clarification?: string;
  avatar_url?: string;
  url?: string;
  image?: string;
  image_url?: string;
  filename?: string;
  prompt_id?: string;
  status?: string;
  code?: string;
  error?: string;
}

async function jsonFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await apiFetch(path, {
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`[API] ${init?.method ?? "GET"} ${path} failed (${res.status}): ${text}`);
  }
  return res.json() as Promise<T>;
}

/** POST /intent */
export function detectIntent(text: string): Promise<IntentResult> {
  return jsonFetch<IntentResult>(`/intent`, {
    method: "POST",
    body: JSON.stringify({ text }),
  });
}

/** Alias for detectIntent for old code */
export const classifyIntent = detectIntent;

/** POST /persona/add_trait */
export function addTrait(text: string): Promise<TraitResponse> {
  return jsonFetch<TraitResponse>(`${PERSONA_PREFIX}/add_trait`, {
    method: "POST",
    body: JSON.stringify({ text }),
  });
}

/** POST /persona/avatar_step */
export function avatarStep(input: { traits?: Traits; reply?: string }): Promise<AvatarStepResponse> {
  return jsonFetch<AvatarStepResponse>(`${PERSONA_PREFIX}/avatar_step`, {
    method: "POST",
    body: JSON.stringify(input ?? {}),
  });
}

/** Alias for avatarStep for old code expecting generateAvatar */
export async function generateAvatar(prompt: string) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 300_000); // allow slow renders
  try {
    const res = await apiFetch(`/persona/generate_avatar`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ prompt }),
      signal: controller.signal,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`generate_avatar failed (${res.status}): ${text}`);
    }
    return res.json();
  } finally {
    clearTimeout(timeout);
  }
}

/** POST /lexi/gen/avatar */
export async function requestAvatarGeneration(
  payload: AvatarGenRequestPayload
): Promise<AvatarGenResponse> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 300_000);
  try {
    // API_BASE already includes "/lexi", so keep path relative.
    const res = await apiFetch(`/gen/avatar`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...payload,
        seed: payload.seed ?? null,
        flux_pipeline: payload.flux_pipeline ?? "flux_v2",
        ...(payload.sd_mode === "img2img" ? { strength: payload.strength ?? 0.35 } : {}),
      }),
      signal: controller.signal,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`[API] gen/avatar failed (${res.status}): ${text}`);
    }
    return res.json() as Promise<AvatarGenResponse>;
  } finally {
    clearTimeout(timeout);
  }
}

export async function fetchAvatarGenerationStatus(
  promptId: string
): Promise<AvatarGenResponse & { status: string }> {
  const res = await apiFetch(`/gen/avatar/status/${promptId}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`[API] GET gen/avatar/status failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** GET /persona */
export function getPersona(): Promise<PersonaState> {
  return jsonFetch<PersonaState>(PERSONA_PREFIX);
}

/** Alias for getPersona for old code expecting fetchPersona */
export const fetchPersona = getPersona;

/** GET /persona/debug/traits */
export function debugTraits(): Promise<DebugTraitsResponse> {
  return jsonFetch<DebugTraitsResponse>(`${PERSONA_PREFIX}/debug/traits`);
}

/** GET /analytics/summary */
export function fetchAnalyticsSummary(): Promise<AnalyticsSummary> {
  return jsonFetch<AnalyticsSummary>(`/analytics/summary`);
}

export interface SendPromptOptions {
  prompt: string;
  intent?: string;
  onChunk?: (delta: string) => void;
  requestId?: string;
}

export const CHAT_ERROR_MESSAGE = "A technical error prevented me from finishing that reply. Please retry, or ask me to continue the previous answer.";
export const CHAT_PENDING_MESSAGE = "The previous reply is still being generated. Please wait a moment, then ask me to continue.";
class ChatPendingError extends Error {}

// Never render an arbitrary exception or server response body in the transcript.
export function chatErrorMessage(error: unknown): string {
  if (error instanceof ChatPendingError) return CHAT_PENDING_MESSAGE;
  return CHAT_ERROR_MESSAGE;
}

async function moderationResponse(response: Response): Promise<TourPromptResponse> {
  if (response.status === 409) {
    const payload = await response.json().catch(() => null);
    if (payload?.detail === "A reply is already being generated in this conversation") {
      throw new ChatPendingError(CHAT_PENDING_MESSAGE);
    }
  }
  if (response.status === 403) {
    const payload = await response.json().catch(() => null);
    if (payload?.safety?.blocked === true && typeof payload.cleaned === "string") {
      const cleaned = sanitizeLlmFooterText(sanitizeUntrustedContext(payload.cleaned)).trim();
      if (cleaned) {
        // Explicit projection: do not pass categories, raw JSON, or session IDs
        // into the successful chat-message path.
        return { cleaned, choices: [{ text: cleaned }] };
      }
    }
  }
  throw new Error(CHAT_ERROR_MESSAGE);
}

export interface TourPromptResponse {
  cleaned?: string;
  raw?: string;
  choices?: Array<{ text?: string }>;
  mode?: string | null;
}

export async function sendTourPrompt(prompt: string, cardId?: string): Promise<TourPromptResponse> {
  const response = await apiFetch(`/tour/prompt`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ prompt, card_id: cardId }),
  });

  if (!response.ok) {
    return moderationResponse(response);
  }

  const payload = (await response.json()) as TourPromptResponse;
  if (typeof payload?.cleaned === "string") {
    payload.cleaned = sanitizeLlmFooterText(sanitizeUntrustedContext(payload.cleaned));
  }
  if (typeof payload?.raw === "string") {
    payload.raw = sanitizeLlmFooterText(sanitizeUntrustedContext(payload.raw));
  }
  if (Array.isArray(payload?.choices)) {
    payload.choices = payload.choices.map((choice) =>
      choice?.text ? { ...choice, text: sanitizeLlmFooterText(sanitizeUntrustedContext(choice.text)) } : choice
    );
  }
  return payload;
}

export async function fetchConversationHistory() {
  if (!getSessionId()) return { messages: [] };
  return jsonFetch<{ session_id?: string; messages: Array<{
    role: string; content: string; message_id: string; completion_status: string;
  }> }>(`/alpha/session/history`);
}

export async function sendPrompt({ prompt, intent, onChunk, requestId }: SendPromptOptions) {
  const body = { prompt, intent, request_id: requestId };
  const response = await apiFetch(`/process`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: onChunk ? "application/x-ndjson" : "application/json",
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const moderated = await moderationResponse(response);
    if (moderated.cleaned) onChunk?.(moderated.cleaned);
    return moderated;
  }

  const reader = response.body?.getReader?.();
  if (!reader) {
    const payload = await response.json();
    if (payload?.cleaned && typeof payload.cleaned === "string") {
      const safeCleaned = sanitizeLlmFooterText(sanitizeUntrustedContext(payload.cleaned));
      if (safeCleaned) {
        onChunk?.(safeCleaned);
      }
      if (typeof payload.raw === "string") {
        payload.raw = sanitizeLlmFooterText(sanitizeUntrustedContext(payload.raw));
      }
      if (Array.isArray(payload.choices)) {
        payload.choices = payload.choices.map((choice: any) =>
          choice?.text
            ? {
                ...choice,
                text: sanitizeLlmFooterText(sanitizeUntrustedContext(String(choice.text))),
              }
            : choice
        );
      }
      return { ...payload, cleaned: safeCleaned };
    }
    return payload;
  }

  const expectsTerminal = response.headers?.get?.("content-type")?.includes("application/x-ndjson");
  let terminalReceived = false;
  const decoder = new TextDecoder();
  let buffer = "";
  let finalPayload: Record<string, any> | null = null;
  let aggregatedRaw = "";
  let aggregatedSanitized = "";

  const processLine = (line: string) => {
    const trimmed = line.trim();
    if (!trimmed) {
      return;
    }
    let data: Record<string, any>;
    try {
      data = JSON.parse(trimmed);
    } catch {
      throw new Error(CHAT_ERROR_MESSAGE);
    }
    if (data.heartbeat === true) return;
    if (typeof data.delta === "string") {
      aggregatedRaw += data.delta;
      const nextSanitized = sanitizeLlmFooterText(sanitizeUntrustedContext(aggregatedRaw));
      const emit = nextSanitized.slice(aggregatedSanitized.length);
      if (emit) {
        onChunk?.(emit);
      }
      aggregatedSanitized = nextSanitized;
      return;
    }
    if (data.error) {
      throw new Error(CHAT_ERROR_MESSAGE);
    }
    if (data.done) {
      terminalReceived = true;
      finalPayload = data;
      return;
    }
    finalPayload = data;
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buffer.indexOf("\n")) >= 0) {
      const line = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 1);
      processLine(line);
    }
  }

  buffer += decoder.decode();
  if (buffer) {
    processLine(buffer);
  }

  if (expectsTerminal && !terminalReceived) throw new Error(CHAT_ERROR_MESSAGE);

  if (!finalPayload) {
    finalPayload = {
      cleaned: aggregatedSanitized,
      raw: aggregatedSanitized,
      choices: aggregatedSanitized ? [{ text: aggregatedSanitized }] : [],
      mode: null,
    };
  }

  if (finalPayload.done) {
    if (typeof finalPayload.cleaned === "string") {
      finalPayload.cleaned = sanitizeLlmFooterText(sanitizeUntrustedContext(finalPayload.cleaned));
      if (!finalPayload.cleaned) {
        finalPayload.cleaned = aggregatedSanitized;
      }
    } else {
      finalPayload.cleaned = aggregatedSanitized;
    }
    if (typeof finalPayload.raw === "string") {
      finalPayload.raw = sanitizeLlmFooterText(sanitizeUntrustedContext(finalPayload.raw));
      if (!finalPayload.raw) {
        finalPayload.raw = aggregatedSanitized;
      }
    } else {
      finalPayload.raw = aggregatedSanitized;
    }
    if (Array.isArray(finalPayload.choices)) {
      finalPayload.choices = finalPayload.choices.map((choice: any) =>
        choice?.text
          ? {
              ...choice,
              text: sanitizeLlmFooterText(sanitizeUntrustedContext(String(choice.text))),
            }
          : choice
      );
    }
    const { done, ...rest } = finalPayload;
    return rest;
  }

  return finalPayload;
}

export async function approveAvatarReviewCandidate(packId: string, selectedGlobalIndex: number) {
  const response = await apiFetch(`/operator/avatar/review-pack/${encodeURIComponent(packId)}/chat-approve`, {
    method: "POST",
    body: JSON.stringify({
      selected_global_index: selectedGlobalIndex,
      reason: "selected in chat",
    }),
  });
  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    throw new Error(
      `[API] avatar review approval failed (${response.status}): ${errText || response.statusText}`
    );
  }
  return response.json();
}

export async function fetchAvatarReviewPack(packId: string): Promise<AvatarReviewPack> {
  const response = await apiFetch(`/operator/avatar/review-pack/${encodeURIComponent(packId)}/chat`, {
    method: "GET",
  });
  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    throw new Error(`[API] avatar review pack fetch failed (${response.status}): ${errText || response.statusText}`);
  }
  return response.json() as Promise<AvatarReviewPack>;
}

const UNTRUSTED_CONTEXT_BLOCK_RE = new RegExp(
  "\\[UNTRUSTED_CONTEXT[^\\r\\n]*(?:\\]|$)[\\s\\S]*?(?:\\[/UNTRUSTED_CONTEXT[^\\r\\n]*(?:\\]|$)|$)",
  "gi",
);
const UNTRUSTED_CONTEXT_LINE_RE =
  /\[\/?UNTRUSTED_CONTEXT[^\r\n]*(?:\]|$)\s*(?:\r?\n\s*\([^)]*\)\s*)*/gi;
const LLM_FOOTER_HINT_BLOCK_RE =
  /(?:^|\r?\n)\*{0,2}[^\n]{0,160}?(?:transition\s+hint|system\s+hint|follow-?up\s+question|emotional\s+check-?in)\b[^\n]*\*{0,2}.*$/i;
const META_PAREN_RE =
  /\(\s*[^)]*?(?:softly|system|nudg|reflect|await|feedback|before\s+proceed|follow-?up|emotional\s+check|transition|hint|prompt|encourag|reassur|suggest|response)[^)]*\)\s*/gi;

function sanitizeUntrustedContext(text: string) {
  const cleaned = text
    .replace(UNTRUSTED_CONTEXT_BLOCK_RE, "")
    .replace(UNTRUSTED_CONTEXT_LINE_RE, "")
    .trim();
  return cleaned;
}

function sanitizeLlmFooterText(text: string) {
  return text
    .replace(LLM_FOOTER_HINT_BLOCK_RE, "")
    .replace(META_PAREN_RE, "")
    .trim();
}

export type LexiEventPayload = {
  type: "system_onboarding";
  mode: "tour" | "skip";
  flags?: Record<string, any>;
};

export interface LexiEventResponse {
  ok: boolean;
  message?: { role?: string; content: string };
  fallback?: boolean;
  tool_used?: boolean;
  tools_contract?: Record<string, any>;
  legal_available?: boolean;
  legal_path?: string;
  skip_message?: string;
  cached?: boolean;
}

export function sendLexiEvent(payload: LexiEventPayload): Promise<LexiEventResponse> {
  return jsonFetch<LexiEventResponse>(`/onboarding/boot`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function fetchTourLegal(): Promise<{ ok?: boolean; text: string }> {
  return jsonFetch<{ ok?: boolean; text: string }>(`/tour/legal`);
}

export interface AuthProviderSummary {
  name: string;
  enabled: boolean;
  status: string;
}

export interface AuthUser {
  id: string;
  email?: string;
  display_name?: string;
  username?: string;
  disclaimer_acked: boolean;
  providers: Array<{
    name?: string;
    email?: string;
    email_verified?: boolean;
    last_used_at?: string;
  }>;
}

export interface AuthMeResponse {
  authenticated: boolean;
  user: AuthUser | null;
}

export interface AuthProvidersResponse {
  providers: AuthProviderSummary[];
}

export interface AuthSignupPayload {
  email: string;
  password: string;
  display_name: string;
  username?: string;
  disclaimer_acknowledged: boolean;
}

export interface AuthLoginPayload {
  email: string;
  password: string;
}

export interface GoogleLoginPayload {
  credential: string;
  g_csrf_token?: string;
}

export interface GoogleLoginOptions {
  onResponseStatus?: (status: number) => void;
}

export interface ProfileUpdatePayload {
  display_name?: string;
  username?: string;
  disclaimer_acknowledged?: boolean;
}

export function authMe(): Promise<AuthMeResponse> {
  return jsonFetch<AuthMeResponse>(`/auth/me`, { method: "GET" }).then((resp) => {
    rememberAuthUserId(resp.user);
    return resp;
  });
}

export function authProviders(): Promise<AuthProvidersResponse> {
  return jsonFetch<AuthProvidersResponse>(`/auth/providers`, { method: "GET" });
}

export function authSignup(payload: AuthSignupPayload): Promise<AuthMeResponse> {
  return jsonFetch<AuthMeResponse>(`/auth/signup`, {
    method: "POST",
    body: JSON.stringify(payload),
  }).then((resp) => {
    rememberAuthUserId(resp.user);
    return resp;
  });
}

export function authLogin(payload: AuthLoginPayload): Promise<AuthMeResponse> {
  return jsonFetch<AuthMeResponse>(`/auth/login`, {
    method: "POST",
    body: JSON.stringify(payload),
  }).then((resp) => {
    rememberAuthUserId(resp.user);
    return resp;
  });
}

export async function authGoogleLogin(
  payload: GoogleLoginPayload,
  options: GoogleLoginOptions = {}
): Promise<AuthMeResponse> {
  const path = `/auth/providers/google/login`;
  const res = await apiFetch(path, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  options.onResponseStatus?.(res.status);
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`[API] POST ${path} failed (${res.status}): ${text}`);
  }
  const resp = (await res.json()) as AuthMeResponse;
  rememberAuthUserId(resp.user);
  return resp;
}

export function authLogout(): Promise<{ ok: boolean }> {
  return jsonFetch<{ ok: boolean }>(`/auth/logout`, {
    method: "POST",
    body: "{}",
  }).then((resp) => {
    rememberAuthUserId(null);
    setSessionId(null);
    return resp;
  });
}

export function profileGet(): Promise<{ user: AuthUser }> {
  return jsonFetch<{ user: AuthUser }>(`/profile`, { method: "GET" }).then((resp) => {
    rememberAuthUserId(resp.user);
    return resp;
  });
}

export function profileUpdate(payload: ProfileUpdatePayload): Promise<{ user: AuthUser }> {
  return jsonFetch<{ user: AuthUser }>(`/profile`, {
    method: "POST",
    body: JSON.stringify(payload),
  }).then((resp) => {
    rememberAuthUserId(resp.user);
    return resp;
  });
}

export type MemoryItemStatus =
  | "active"
  | "stale"
  | "superseded"
  | "revoked"
  | "soft_deleted"
  | string;

export type MemoryFeedbackType =
  | "helpful"
  | "incorrect"
  | "forget"
  | "not_important"
  | "always_remember";

export interface MemoryListItem {
  id: string;
  item_type: string;
  scope: string;
  title: string | null;
  content_text: string;
  confidence: number;
  importance: number;
  stability: number;
  sensitivity: string;
  status: MemoryItemStatus;
  created_at: string;
  updated_at: string;
  last_confirmed_at: string | null;
  expires_at: string | null;
  is_pinned: boolean;
}

export interface MemoryListResponse {
  items: MemoryListItem[];
  limit: number;
  offset: number;
  count: number;
}

export interface ListMemoryItemsParams {
  itemType?: string[];
  scope?: string[];
  status?: string[];
  pinned?: boolean;
  limit?: number;
  offset?: number;
}

export interface MemoryItemDetail extends Omit<MemoryListItem, "is_pinned"> {
  memory_space_id: string;
  normalized_json: Record<string, unknown> | null;
  supersedes_item_id: string | null;
  source_summary: string | null;
  last_accessed_at: string | null;
}

export interface MemoryEvidenceRow {
  evidence_id: string;
  evidence_type: string;
  weight: number;
  evidence_created_at: string;
  event_id: string;
  event_type: string;
  content_text: string;
  source_ref: string | null;
  actor_type: string;
  actor_ref: string | null;
  event_created_at: string;
}

export interface MemoryFeedbackRecord {
  id: string;
  memory_item_id: string;
  feedback_type: MemoryFeedbackType | string;
  notes: string | null;
  created_at: string;
  actor_type: string;
  actor_ref: string | null;
}

export interface MemoryLineageItem {
  id: string;
  item_type: string;
  scope: string;
  title: string | null;
  status: string;
  updated_at: string;
}

export interface MemoryExplainability {
  status: string;
  supersedes_item_id: string | null;
  evidence_count: number;
  is_pinned: boolean;
  feedback_count: number;
  feedback_summary: Record<string, unknown>;
  lifecycle_reason: string;
  lineage: {
    supersedes: MemoryLineageItem | null;
    superseded_by: MemoryLineageItem[];
  };
}

export interface MemoryItemExplanationResponse {
  item: MemoryItemDetail;
  evidence: MemoryEvidenceRow[];
  feedback: MemoryFeedbackRecord[];
  explainability: MemoryExplainability;
}

export interface MemoryItemMutationResponse {
  ok: boolean;
  item?: MemoryItemDetail;
  status?: string;
  is_pinned?: boolean;
  item_id?: string;
  action?: string;
}

function joinCsv(values?: string[]): string | null {
  if (!values || !values.length) return null;
  const normalized = values.map((value) => value.trim()).filter(Boolean);
  return normalized.length ? normalized.join(",") : null;
}

export function listMemoryItems(params: ListMemoryItemsParams = {}): Promise<MemoryListResponse> {
  const search = new URLSearchParams();
  const itemType = joinCsv(params.itemType);
  const scope = joinCsv(params.scope);
  const status = joinCsv(params.status);
  if (itemType) search.set("item_type", itemType);
  if (scope) search.set("scope", scope);
  if (status) search.set("status", status);
  if (typeof params.pinned === "boolean") search.set("pinned", String(params.pinned));
  if (typeof params.limit === "number") search.set("limit", String(params.limit));
  if (typeof params.offset === "number") search.set("offset", String(params.offset));
  const query = search.toString();
  const suffix = query ? `?${query}` : "";
  return jsonFetch<MemoryListResponse>(`/memory/items${suffix}`, { method: "GET" });
}

export function explainMemoryItem(itemId: string): Promise<MemoryItemExplanationResponse> {
  return jsonFetch<MemoryItemExplanationResponse>(`/memory/items/${encodeURIComponent(itemId)}`, {
    method: "GET",
  });
}

export function pinMemoryItem(itemId: string): Promise<MemoryItemMutationResponse> {
  return jsonFetch<MemoryItemMutationResponse>(`/memory/items/${encodeURIComponent(itemId)}/pin`, {
    method: "POST",
    body: "{}",
  });
}

export function unpinMemoryItem(itemId: string): Promise<MemoryItemMutationResponse> {
  return jsonFetch<MemoryItemMutationResponse>(`/memory/items/${encodeURIComponent(itemId)}/unpin`, {
    method: "POST",
    body: "{}",
  });
}

export function revokeMemoryItem(
  itemId: string,
  payload?: { notes?: string | null }
): Promise<MemoryItemMutationResponse> {
  return jsonFetch<MemoryItemMutationResponse>(`/memory/items/${encodeURIComponent(itemId)}/revoke`, {
    method: "POST",
    body: JSON.stringify({ notes: payload?.notes || undefined }),
  });
}

export function forgetMemoryItem(
  itemId: string,
  payload?: { soft_delete?: boolean; notes?: string | null }
): Promise<MemoryItemMutationResponse> {
  return jsonFetch<MemoryItemMutationResponse>(`/memory/items/${encodeURIComponent(itemId)}/forget`, {
    method: "POST",
    body: JSON.stringify({
      soft_delete: Boolean(payload?.soft_delete),
      notes: payload?.notes || undefined,
    }),
  });
}

export function submitMemoryFeedback(
  itemId: string,
  payload: { feedback_type: MemoryFeedbackType; notes?: string | null }
): Promise<MemoryItemMutationResponse> {
  return jsonFetch<MemoryItemMutationResponse>(`/memory/items/${encodeURIComponent(itemId)}/feedback`, {
    method: "POST",
    body: JSON.stringify({
      feedback_type: payload.feedback_type,
      notes: payload.notes || undefined,
    }),
  });
}

export type EntryMode = "new" | "returning";

export interface AccountBootstrapReq {
  identifier: string;
  entry_mode: EntryMode;
  attempt_count?: number;
}

export type AccountBootstrapStatus = "CREATED_NEW" | "FOUND_EXISTING" | "EXISTS_CONFLICT" | "NOT_FOUND";

export interface AccountBootstrapResp {
  status: AccountBootstrapStatus;
  user_id?: string;
  display_name?: string;
  has_seen_disclaimer?: boolean;
}

export async function apiAccountBootstrap(payload: AccountBootstrapReq): Promise<AccountBootstrapResp> {
  const res = await apiFetch(`/account/bootstrap`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error("Account bootstrap failed");
  }
  return res.json();
}

export async function apiDisclaimerPreload(entry_mode: EntryMode): Promise<void> {
  const res = await apiFetch(`/onboarding/disclaimer_preload`, {
    method: "POST",
    body: JSON.stringify({ entry_mode }),
  });
  if (!res.ok) {
    throw new Error("Disclaimer preload failed");
  }
}

export interface DisclaimerCachedResp {
  status: "OK" | "EMPTY" | "NO_SESSION";
  disclaimer?: string;
}

export async function apiDisclaimerCached(): Promise<DisclaimerCachedResp> {
  const res = await apiFetch(`/onboarding/disclaimer_cached`);
  if (!res.ok) {
    throw new Error("Disclaimer cached fetch failed");
  }
  return res.json();
}

export async function apiDisclaimerAck(
  user_id: string | null | undefined,
  accepted: boolean,
  version = "v1"
): Promise<void> {
  const res = await apiFetch(`/account/disclaimer_ack`, {
    method: "POST",
    body: JSON.stringify({ ...(user_id ? { user_id } : {}), accepted, version }),
  });
  if (!res.ok) {
    throw new Error("Disclaimer ack failed");
  }
}

export type AlphaTourStep = {
  slug: string;
  prompt: string;
  narration: string;
};

export interface AlphaTourScriptResponse {
  steps: AlphaTourStep[];
  onboarding?: Record<string, any>;
  alpha_strict?: boolean;
}

export interface AlphaSessionResponse {
  session_id: string;
  consent: boolean;
  variant?: string | null;
  alpha_strict: boolean;
}

let pendingSession: { owner: string | null; promise: Promise<Pick<AlphaSessionResponse, "session_id">> } | null = null;

/** Resume a verified owned conversation. Network failure must not discard it. */
export async function ensureAlphaSession(): Promise<Pick<AlphaSessionResponse, "session_id">> {
  const owner = getUserId();
  if (pendingSession && pendingSession.owner === owner) return pendingSession.promise;
  const promise = (async () => {
    const existing = getSessionId();
    if (existing) {
      const response = await apiFetch(`/alpha/session/history`);
      if (getUserId() !== owner) throw new Error("Account changed while resuming conversation");
      if (response.ok) return { session_id: existing };
      if (![401, 403, 404].includes(response.status)) throw new Error(CHAT_ERROR_MESSAGE);
      setSessionId(null);
    }
    return startAlphaSession();
  })();
  pendingSession = { owner, promise };
  try { return await promise; }
  finally { if (pendingSession?.promise === promise) pendingSession = null; }
}

export async function startAlphaSession(payload?: {
  consent?: boolean;
  variant?: string | null;
  tags?: string[];
}): Promise<AlphaSessionResponse> {
  const ownerAtStart = getUserId();
  const body = JSON.stringify({
    consent: payload?.consent ?? true,
    variant: payload?.variant ?? null,
    tags: payload?.tags ?? ["alpha"],
  });
  const data = await jsonFetch<AlphaSessionResponse>(`/alpha/session/start`, {
    method: "POST",
    body,
  });
  if (getUserId() !== ownerAtStart) throw new Error("Account changed during conversation creation");
  setSessionId(data.session_id);
  return data;
}

export async function updateAlphaConsent(consent: boolean): Promise<{ consent: boolean }> {
  return jsonFetch<{ consent: boolean }>(`/alpha/session/consent`, {
    method: "POST",
    body: JSON.stringify({ consent }),
  });
}

export async function endAlphaSession(): Promise<{ archived: boolean; archive_path?: string }> {
  const res = await jsonFetch<{ archived: boolean; archive_path?: string }>(`/alpha/session/end`, {
    method: "POST",
    body: "{}",
  });
  setSessionId(null);
  return res;
}

export async function fetchAlphaTourScript(): Promise<AlphaTourScriptResponse> {
  return jsonFetch<AlphaTourScriptResponse>(`/alpha/tour/script`);
}

export async function requestTourAvatarPreview(
  prompt: string
): Promise<{ preview_url?: string; url?: string; alpha_strict?: boolean }> {
  return jsonFetch(`/alpha/tour/avatar-preview`, {
    method: "POST",
    body: JSON.stringify({ prompt }),
  });
}

export async function submitTourNowTopic(topic: string): Promise<{ now_topic: string }> {
  return jsonFetch(`/alpha/tour/now-topic`, {
    method: "POST",
    body: JSON.stringify({ topic }),
  });
}

export async function submitTourMemoryNote(note: string): Promise<{ ack: boolean }> {
  return jsonFetch(`/alpha/tour/memory-note`, {
    method: "POST",
    body: JSON.stringify({ note }),
  });
}

export async function postAlphaTourMemory(body: { note: string }): Promise<{ ok: boolean; ack?: boolean }> {
  return jsonFetch(`/alpha/tour/memory`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function sendTourFeedback(helpful: boolean, comment?: string): Promise<{ ok: boolean }> {
  return jsonFetch(`/alpha/tour/feedback`, {
    method: "POST",
    body: JSON.stringify({ helpful, comment }),
  });
}

export async function postAlphaMetric(
  event: string,
  detail?: Record<string, unknown>
): Promise<{ ok: boolean }> {
  return jsonFetch(`/alpha/session/metrics`, {
    method: "POST",
    body: JSON.stringify({ event, detail }),
  });
}

export async function downloadSessionMemory(): Promise<{ blob: Blob; filename: string }> {
  const res = await apiFetch(`/alpha/session/memory`, {
    method: "GET",
    headers: { Accept: "application/jsonl" },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`[API] GET /alpha/session/memory failed (${res.status}): ${text}`);
  }
  const disposition = res.headers.get("content-disposition") || "";
  const match = disposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
  const filename = decodeURIComponent(match?.[1] || match?.[2] || "memory.jsonl");
  const blob = await res.blob();
  return { blob, filename };
}

export type Persona = PersonaState;

export const API = {
  detectIntent,
  classifyIntent,
  addTrait,
  avatarStep,
  generateAvatar,
  getPersona,
  fetchPersona,
  debugTraits,
  fetchAnalyticsSummary,
  requestAvatarGeneration,
  fetchAvatarGenerationStatus,
  sendPrompt,
  apiFetch,
  startAlphaSession,
  updateAlphaConsent,
  endAlphaSession,
  fetchAlphaTourScript,
  requestTourAvatarPreview,
  submitTourNowTopic,
  submitTourMemoryNote,
  postAlphaTourMemory,
  sendTourFeedback,
  postAlphaMetric,
  downloadSessionMemory,
  sendLexiEvent,
  fetchTourLegal,
  authMe,
  authProviders,
  authSignup,
  authLogin,
  authGoogleLogin,
  authLogout,
  profileGet,
  profileUpdate,
  listMemoryItems,
  explainMemoryItem,
  pinMemoryItem,
  unpinMemoryItem,
  revokeMemoryItem,
  forgetMemoryItem,
  submitMemoryFeedback,
};

export { API_BASE as BACKEND };
export default API;
